import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { MensajesRequestFields } from "./api/schemas";
import { useForm } from "react-hook-form";
import { useMediaQuery, useTheme } from "@mui/material";
import { usePagination } from "src/hooks/usePagination";
import { HttpMessages } from "src/http/workflow/httpMessages";
import { Mensaje } from "./types";
import MessagesMobile from "./components/mobile/MessagesMobile";
import { MessagesDesktop } from "./components/desktop/MessagesDesktop";
import { useEffect, useRef, useState } from "react";
import { useDebounceCallback } from "src/hooks/useDebounceCallback";
import { useLoader } from "src/hooks/useLoader";
import { HttpCacheWorkflow } from "../../http/workflow/httpCacheWorkflow";
import { HttpSystems } from "src/http/workflow/httpSystems";
import { EntityWithIdFields } from "src/types";
import {filterStorage} from "../../utils/localStorage/messagesFilterStorage";

export const schemaFiltroMensajes = z.object({
    [MensajesRequestFields.CodTipoMensaje]: z.array(z.number()),
    [MensajesRequestFields.CodsSistemaPrincipal]: z.array(z.number()),
    [MensajesRequestFields.Referencia]: z.string().optional(),
    [MensajesRequestFields.IdMensaje]: z.string().optional(),
    [MensajesRequestFields.FechaDesde]: z.date().nullish(),
    [MensajesRequestFields.FechaHasta]: z.date().nullish(),
    [MensajesRequestFields.Estados]: z.array(z.number()),
    [MensajesRequestFields.IsResponsible]: z.boolean()
})

export type SchemaFiltroMensajeType = z.infer<typeof schemaFiltroMensajes>

type DefaultValues = {
    [MensajesRequestFields.CodsSistemaPrincipal]: number[],
    [MensajesRequestFields.CodTipoMensaje]: number[],
    [MensajesRequestFields.Estados]: number[],
    [MensajesRequestFields.IsResponsible]: boolean
}

export const Messages = () => {
    
   
    const loadDefaultValues = () => {
  
        return new Promise<SchemaFiltroMensajeType>((resolve, reject) => {
            const storedValues = filterStorage.load()
            if (storedValues) {
                resolve(storedValues)
            } else {
                resolve({
                    [MensajesRequestFields.CodTipoMensaje]: [],
                    [MensajesRequestFields.CodsSistemaPrincipal]: [],
                    [MensajesRequestFields.Referencia]: undefined,
                    [MensajesRequestFields.IdMensaje]: undefined,
                    [MensajesRequestFields.FechaDesde]: null,
                    [MensajesRequestFields.FechaHasta]: null,
                    [MensajesRequestFields.Estados]: [1], // "en curso"
                    [MensajesRequestFields.IsResponsible]: true
                })
            }
        })
        
    }

    const form = useForm<SchemaFiltroMensajeType>({
        resolver: zodResolver(schemaFiltroMensajes),
        defaultValues: loadDefaultValues
    })

    const query = usePagination<Mensaje, SchemaFiltroMensajeType>({
        loader: (pagination, filter) => HttpMessages.getByUser(filter, pagination)
    })

    const onSearch = (data: SchemaFiltroMensajeType) => {

        const params = {
            ...data,
            [MensajesRequestFields.Referencia]:
                !data[MensajesRequestFields.Referencia]?.length ? undefined : data[MensajesRequestFields.Referencia], // TODO: esto es un parche para que cuando la referencia es '' (empty string), el parametro del query sea undefined, en vez de nada.
        }
        filterStorage.save(params)
        query.setFilterAndSearch(params)
    }

    const { current: handleSearch } = useRef(() => form.handleSubmit(onSearch, console.error)())

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const debouncedSearch = useDebounceCallback(handleSearch, 200)

    useEffect(() => {
        const subscription = form.watch((value, { name, type }) => {
            if (name === MensajesRequestFields.Referencia || name === MensajesRequestFields.IdMensaje) {
                debouncedSearch()
            } else {
                handleSearch()
            }
        })
        
        return () => subscription.unsubscribe()
    }, [form.watch])


    const { hideLoader } = useLoader()

    // TODO: esto es parche, en operaciones habrá que mejorar el block ui
    useEffect(() => {
        hideLoader()
    }, [])

    if (!(form.watch(MensajesRequestFields.CodsSistemaPrincipal) && form.watch(MensajesRequestFields.CodTipoMensaje))) {
        return (<></>)
    }

    return (
        isSmallScreen ?
            <MessagesMobile query={query} handleSearch={handleSearch} form={form} /> :
            <MessagesDesktop query={query} handleSearch={handleSearch} form={form} />
    )
}

export type MessagesComponentProps = {
    query: ReturnType<typeof usePagination<Mensaje, SchemaFiltroMensajeType>>
    handleSearch: () => void
    form: ReturnType<typeof useForm<SchemaFiltroMensajeType>>
}
